import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import { color } from '../../app/theme';
import config from '../../app/config';
import Button from './Button';
import TextField from './TextField';
import { validateCellPhone, emailValid } from '../../app/utils';

const useStyles = makeStyles(
  {
    root: {
      padding: '24px',
      border: `1px solid ${color.$10}`,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
    title: {
      fontSize: '15px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      color: color.$13,
      marginBottom: 16,
      textAlign: 'center',
    },
    logoImageUrl: {
      width: 80,
      height: 80,
      margin: '0 auto 16px',
    },
    input: {
      width: '100%',
    },
    sendVerification: {
      color: color.$1,
      fontSize: '13px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      textDecoration: 'underline',
      textAlign: 'right',
      margin: '8px 0 24px 0',
      cursor: 'pointer',
    },
    disableSendVerification: {
      color: color.$10,
      fontSize: '13px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      textDecoration: 'underline',
      textAlign: 'right',
      margin: '8px 0 24px 0',
      cursor: 'not-allowed',
    },
    tip: {
      fontSize: '13px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1.38',
      color: color.$13,
      marginTop: 16,
    },
    error: {
      color: color.$17,
    },
  },
  {
    name: 'BindPage',
  },
);

let timer = null;

const BindPage = ({
  account,
  setAccount,
  code,
  setCode,
  data,
  isLoading,
  onClick = () => {},
  onSendVerification = () => {},
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { bindPage } = data || {};
  const {
    pageTitle,
    btnBgColor: bindBtnBackgroundColor,
    logoImageUrl,
    accountPlaceHolder,
  } = bindPage || {};

  const [isOn, setIsOn] = useState(false);
  const [sec, setSec] = useState(60);
  const [isAccountError, setIsAccountError] = useState(false);
  const [accountType, setAccountType] = useState('');

  const handleSend = async () => {
    let time = sec;

    setIsOn(true);

    await onSendVerification();

    timer = setInterval(() => {
      time -= 1;

      if (time > 0) {
        setSec(time);
      } else {
        clearInterval(timer);
        setSec(60);
        setIsOn(false);
      }
    }, 1000);
  };

  const clear = () => {
    clearInterval(timer);
    setSec(60);
    setIsOn(false);
    setAccountType('');
    setIsAccountError(false);
  };

  useEffect(() => {
    clearInterval(timer);
    setSec(60);
    setIsOn(false);
    setAccountType('');
    setIsAccountError(false);
  }, []);

  useEffect(() => {
    if (isAccountError) {
      setAccountType('');
    }
  }, [isAccountError]);

  return (
    <div className={classes.root}>
      <div className={classes.title}>
        {pageTitle || t('ecBinding:bindPage')}
      </div>
      <img
        className={classes.logoImageUrl}
        src={logoImageUrl || config.DEFAULT_LOGO_IMAGE_URL}
        alt="logoImageUrl"
      />
      <TextField
        className={classes.input}
        type="text"
        isValid
        value={account}
        placeholder={accountPlaceHolder || t('ecBinding:account-placeholder')}
        onChange={e => {
          setAccount(e.target.value);
        }}
        isError={isAccountError}
      />
      {isOn ? (
        <div className={classes.disableSendVerification}>
          {t('ecBinding:disableSendVerification', { sec })}
        </div>
      ) : (
        <div
          className={classnames(classes.sendVerification, {
            [classes.disableSendVerification]: !account,
          })}
          onClick={() => {
            if (account) {
              const isMail = emailValid(account);
              const isPhone = validateCellPhone(account);

              if (isPhone || isMail) {
                setIsAccountError(false);
                if (isPhone) setAccountType('phone');
                if (isMail) setAccountType('mail');
                handleSend();
              } else {
                setIsAccountError(true);
              }
            }
          }}
        >
          {t('ecBinding:sendVerification')}
        </div>
      )}
      <TextField
        className={classes.input}
        type="text"
        isValid
        value={code}
        placeholder={t('ecBinding:code-placeholder')}
        onChange={e => {
          setCode(e.target.value);
        }}
      />
      {isAccountError && (
        <div className={classnames(classes.tip, classes.error)}>
          {t('ecBinding:sendVerification-error')}
        </div>
      )}
      {!isAccountError && accountType === 'phone' && (
        <div className={classnames(classes.tip)}>
          {t('ecBinding:sendVerification-success-phone')}
        </div>
      )}
      {!isAccountError && accountType === 'mail' && (
        <div className={classes.tip}>
          {t('ecBinding:sendVerification-success-mail')}
        </div>
      )}
      <Button
        style={{
          margin:
            !isAccountError && accountType === '' ? '24px 0 0' : '16px 0 0',
          backgroundColor: bindBtnBackgroundColor || color.$1,
        }}
        topic="primaryGreen"
        isLoading={isLoading}
        isDisable={!code}
        onClick={() => {
          onClick(clear);
        }}
      >
        {t('ecBinding:bind')}
      </Button>
    </div>
  );
};

export default BindPage;
